/**
 * FeedbackWidget - Feedback panel with screenshot capability
 * Allows users to capture screenshots and annotate issues on the page
 *
 * Opened from the "Feedback" button in the top bar (as in Engage and Ember),
 * which asks for it with the `open-feedback` shell event. It used to float its
 * own button over the bottom-right corner of every page, on top of whatever
 * was there.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { useCreateFeedback, useAllFeedback, useAllFeatureSuggestions } from '@/hooks/useFeedback';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { formatRelativeTime } from '@/lib/formatters';
import {
  Camera,
  X,
  Bug,
  Lightbulb,
  HelpCircle,
  MoreHorizontal,
  Check,
  Loader2,
  Crop,
  Trash2,
  ChevronLeft,
  Clock,
  CheckCircle,
  Eye,
  XCircle,
  AlertCircle,
  ClipboardList,
  Crosshair,
} from 'lucide-react';
import type {
  FeedbackType, FeedbackStatus, FeatureSuggestionStatus, SelectedRegion,
  RegionRect, ElementRegion, BrowserInfo,
} from '@/types/feedback';
import { useElementPicker } from './useElementPicker';
import { useShellEvent } from '@/components/layout/shellEvents';
import { usePermissions } from '@/hooks/usePermissions';


// buildElementSelector / describeElement / SelectorNode moved to
// useElementPicker.ts (the shared picker) and re-exported here so the
// existing tests and imports keep working.
export { buildElementSelector, describeElement, type SelectorNode } from './useElementPicker';

// Status display configs
const FEEDBACK_STATUS: Record<FeedbackStatus, { label: string; className: string; icon: typeof Clock }> = {
  new:         { label: 'New',         className: 'bg-blue-100 text-blue-700',   icon: AlertCircle },
  reviewed:    { label: 'Reviewed',    className: 'bg-purple-100 text-purple-700', icon: Eye },
  in_progress: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-700', icon: Clock },
  resolved:    { label: 'Resolved',    className: 'bg-green-100 text-green-700',  icon: CheckCircle },
  wont_fix:    { label: "Won't Fix",   className: 'bg-gray-100 text-gray-600',    icon: XCircle },
};

const SUGGESTION_STATUS: Record<FeatureSuggestionStatus, { label: string; className: string }> = {
  submitted:    { label: 'Submitted',    className: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', className: 'bg-purple-100 text-purple-700' },
  planned:      { label: 'Planned',      className: 'bg-cyan-100 text-cyan-700' },
  in_progress:  { label: 'In Progress',  className: 'bg-yellow-100 text-yellow-700' },
  completed:    { label: 'Completed',    className: 'bg-green-100 text-green-700' },
  declined:     { label: 'Declined',     className: 'bg-gray-100 text-gray-600' },
};

const FEEDBACK_TYPES = [
  { value: 'bug' as FeedbackType, label: 'Bug Report', icon: Bug, color: 'text-red-500' },
  { value: 'improvement' as FeedbackType, label: 'Improvement', icon: Lightbulb, color: 'text-yellow-500' },
  { value: 'confusion' as FeedbackType, label: 'Confusion', icon: HelpCircle, color: 'text-blue-500' },
  { value: 'other' as FeedbackType, label: 'Other', icon: MoreHorizontal, color: 'text-gray-500' },
];

interface FeedbackWidgetProps {
  onSuggestFeature: () => void;
}

export function FeedbackWidget({ onSuggestFeature }: FeedbackWidgetProps) {
  const { user, profile } = useAuth();
  const { user: msUser } = useMicrosoftAuth();
  const createFeedback = useCreateFeedback();

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'type' | 'capture' | 'form' | 'history'>('type');
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotDimensions, setScreenshotDimensions] = useState<{ width: number; height: number } | null>(null);
  /** PREVIEW-px rect while drawing/showing; converted to screenshot px at
   *  submit via `previewScale` (captured at mouse-up so a later panel resize
   *  cannot skew it). */
  const [selectedRegion, setSelectedRegion] = useState<RegionRect | null>(null);
  const [previewScale, setPreviewScale] = useState(1);
  /** A pinpointed page element — wins over the rect when present. */
  const [pinnedElement, setPinnedElement] = useState<ElementRegion | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [currentSelection, setCurrentSelection] = useState<RegionRect | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Reset state when closing
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setStep('type');
    setFeedbackType(null);
    setDescription('');
    setScreenshot(null);
    setScreenshotDimensions(null);
    setSelectedRegion(null);
    setPinnedElement(null);
    setIsPicking(false);
    setIsCapturing(false);
    setIsSelecting(false);
  }, []);

  // ── element pinpointing — the shared picker (useElementPicker), so this
  // widget and the portal's feedback modal cannot drift apart. ──────────────
  const { isPicking, setIsPicking, hoverRect } = useElementPicker({
    onPicked: (pin) => {
      setPinnedElement(pin);
      setSelectedRegion(null);
      setIsOpen(true);
    },
    onExit: () => setIsOpen(true),
  });

  // Capture screenshot using canvas
  const captureScreenshot = useCallback(async () => {
    setIsCapturing(true);
    setIsOpen(false); // Hide widget during capture

    try {
      // Wait for widget to hide
      await new Promise(resolve => setTimeout(resolve, 100));

      // Use html2canvas-like approach with canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      // Set canvas size to viewport
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;

      // Draw a representation of the page
      // Since we can't access actual rendering, we'll use a workaround
      // by drawing the visible elements
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Convert to data URL
      // Note: For actual screenshots, you'd use html2canvas library
      // For now, we'll prompt user to paste their own screenshot or use browser dev tools

      // Instead, let's use the native screenshot API if available
      if ('mediaDevices' in navigator && 'getDisplayMedia' in navigator.mediaDevices) {
        try {
          const stream = await (navigator.mediaDevices as any).getDisplayMedia({
            video: { displaySurface: 'browser' },
            preferCurrentTab: true,
          });

          const video = document.createElement('video');
          video.srcObject = stream;
          await video.play();

          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);

          stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());

          const dataUrl = canvas.toDataURL('image/png');
          setScreenshot(dataUrl);
          setScreenshotDimensions({ width: canvas.width, height: canvas.height });
        } catch {
          // User cancelled or API not available
        }
      }
    } catch (error) {
      console.error('Screenshot capture failed:', error);
    } finally {
      setIsCapturing(false);
      setIsOpen(true);
      setStep('form');
    }
  }, []);

  // Handle region selection on screenshot
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setSelectionStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, [isSelecting]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !selectionStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    setCurrentSelection({
      x: Math.min(selectionStart.x, currentX),
      y: Math.min(selectionStart.y, currentY),
      width: Math.abs(currentX - selectionStart.x),
      height: Math.abs(currentY - selectionStart.y),
    });
  }, [isSelecting, selectionStart]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !currentSelection) return;
    // Capture the preview→screenshot scale NOW, against the element the drag
    // actually happened on. Storing preview px raw was the original bug: the
    // admin view divides by screenshot_width, so a 350px-preview rect rendered
    // as a sliver in the wrong corner.
    const previewWidth = e.currentTarget.getBoundingClientRect().width;
    if (screenshotDimensions && previewWidth > 0) {
      setPreviewScale(screenshotDimensions.width / previewWidth);
    }
    setSelectedRegion(currentSelection);
    setPinnedElement(null);
    setCurrentSelection(null);
    setSelectionStart(null);
    setIsSelecting(false);
  }, [isSelecting, currentSelection, screenshotDimensions]);

  // Get browser info
  const getBrowserInfo = useCallback((): BrowserInfo => {
    return {
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };
  }, []);

  // Resolve user from either Supabase or Microsoft SSO
  const currentUserId = user?.id || msUser?.email || '';
  const currentUserName = profile?.full_name || msUser?.name || undefined;
  const currentUserEmail = user?.email || msUser?.email || undefined;
  const isSignedIn = !!user || !!msUser;
  // Settings › Feedback shows an admin every item and anyone else their own, so
  // "Open the conversation" is offered only where it will actually open.
  const { isAdminUser } = usePermissions();

  // All submissions data (only fetched when history panel is open)
  const { data: allFeedback, isLoading: allFeedbackLoading } = useAllFeedback(step === 'history');
  const { data: allSuggestions, isLoading: allSuggestionsLoading } = useAllFeatureSuggestions(step === 'history');
  const totalSubmissions = (allFeedback?.length ?? 0) + (allSuggestions?.length ?? 0);

  // Submit feedback
  const handleSubmit = useCallback(async () => {
    if (!feedbackType || !description.trim() || !isSignedIn) return;

    // The pinned element wins; otherwise the drag-rect, CONVERTED to native
    // screenshot pixels and stamped with its space so the admin view knows
    // exactly how to render it (legacy unstamped rows were preview-px).
    const region: SelectedRegion | undefined = pinnedElement
      ?? (selectedRegion
        ? {
            kind: 'rect' as const,
            space: 'screenshot' as const,
            x: Math.round(selectedRegion.x * previewScale),
            y: Math.round(selectedRegion.y * previewScale),
            width: Math.round(selectedRegion.width * previewScale),
            height: Math.round(selectedRegion.height * previewScale),
          }
        : undefined);

    try {
      await createFeedback.mutateAsync({
        formData: {
          feedback_type: feedbackType,
          description: description.trim(),
          screenshot_data: screenshot || undefined,
          screenshot_width: screenshotDimensions?.width,
          screenshot_height: screenshotDimensions?.height,
          selected_region: region,
        },
        user: {
          id: currentUserId,
          name: currentUserName,
          email: currentUserEmail,
        },
        pageInfo: {
          url: window.location.href,
          title: document.title,
        },
        browserInfo: getBrowserInfo(),
      });

      handleClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  }, [feedbackType, description, isSignedIn, currentUserId, currentUserName, currentUserEmail, screenshot, screenshotDimensions, selectedRegion, pinnedElement, previewScale, createFeedback, getBrowserInfo, handleClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // The top bar's Feedback button (and anything else in the shell) opens the
  // panel by asking for it.
  useShellEvent('open-feedback', () => setIsOpen(true));

  if (!isSignedIn) return null;

  return (
    <>
      {/* Element-pick mode: instruction banner + hover outline. The document
          listeners in the isPicking effect do the actual work; these visuals
          are pointer-events-none so elementFromPoint sees the real page. */}
      {isPicking && createPortal(
        <div data-feedback-widget className="pointer-events-none fixed inset-0 z-[60]">
          <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-gray-900/90 px-4 py-2 text-sm text-white shadow-lg">
            Click the element you want to point at — Esc to cancel
          </div>
          {hoverRect && (
            <div
              className="absolute rounded border-2 border-primary-500 bg-primary-500/10"
              style={{
                left: hoverRect.x, top: hoverRect.y,
                width: hoverRect.width, height: hoverRect.height,
              }}
            />
          )}
        </div>,
        document.body,
      )}

      {/* Feedback Panel — anchored under the top bar's Feedback button */}
      {isOpen && createPortal(
        <div
          data-feedback-widget
          className="fixed inset-0 z-50 flex items-start justify-end px-3 pb-3 pt-[calc(var(--header-height)+0.5rem)] sm:px-6 pointer-events-none"
        >
          <Card
            padding="none"
            role="dialog"
            aria-label="Send feedback"
            className="w-96 max-w-full max-h-[calc(100vh-var(--header-height)-1.5rem)] overflow-hidden shadow-overlay pointer-events-auto animate-fade-in"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-primary-50 dark:bg-primary-900/20">
              <div className="flex items-center gap-2">
                {step === 'history' && (
                  <button
                    onClick={() => setStep('type')}
                    className="p-1 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-white/50 -ml-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {step === 'history' ? 'All Submissions' : 'Send Feedback'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {step === 'history' ? 'Team feedback & suggestions' : 'Help us improve Flow'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Step 1: Select Type */}
            {step === 'type' && (
              <div className="p-4 space-y-3">
                <p className="text-sm text-gray-600">What type of feedback do you have?</p>
                <div className="grid grid-cols-2 gap-2">
                  {FEEDBACK_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setFeedbackType(type.value);
                        setStep('capture');
                      }}
                      className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                    >
                      <type.icon className={cn('h-6 w-6', type.color)} />
                      <span className="text-sm font-medium text-gray-700">{type.label}</span>
                    </button>
                  ))}
                </div>

                <div className="pt-3 border-t border-gray-200 space-y-1">
                  <button
                    onClick={onSuggestFeature}
                    className="w-full flex items-center justify-center gap-2 p-3 text-sm text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <Lightbulb className="h-4 w-4" />
                    Suggest a new feature
                  </button>
                  <button
                    onClick={() => setStep('history')}
                    className="w-full flex items-center justify-center gap-2 p-2.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <ClipboardList className="h-4 w-4" />
                    View all submissions
                    {totalSubmissions > 0 && (
                      <span className="ml-1 bg-gray-200 text-gray-700 text-xs font-medium px-1.5 py-0.5 rounded-full">
                        {totalSubmissions}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Capture Screenshot */}
            {step === 'capture' && (
              <div className="p-4 space-y-4">
                <div className="flex items-center gap-2">
                  {FEEDBACK_TYPES.find(t => t.value === feedbackType)?.icon && (
                    <span className={cn(FEEDBACK_TYPES.find(t => t.value === feedbackType)?.color)}>
                      {(() => {
                        const Icon = FEEDBACK_TYPES.find(t => t.value === feedbackType)?.icon;
                        return Icon ? <Icon className="h-5 w-5" /> : null;
                      })()}
                    </span>
                  )}
                  <span className="font-medium text-gray-900">
                    {FEEDBACK_TYPES.find(t => t.value === feedbackType)?.label}
                  </span>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-gray-600">Would you like to capture a screenshot?</p>

                  <button
                    onClick={captureScreenshot}
                    disabled={isCapturing}
                    className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors"
                  >
                    {isCapturing ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
                    ) : (
                      <Camera className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-600">
                      {isCapturing ? 'Capturing...' : 'Capture Screenshot'}
                    </span>
                  </button>

                  <button
                    onClick={() => setStep('form')}
                    className="w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Skip screenshot
                  </button>
                </div>
              </div>
            )}

            {/* History: All Submissions */}
            {step === 'history' && (
              <div className="overflow-y-auto max-h-[65vh]">
                {(allFeedbackLoading || allSuggestionsLoading) ? (
                  <div className="flex justify-center py-10">
                    <Spinner size="md" />
                  </div>
                ) : (allFeedback?.length ?? 0) === 0 && (allSuggestions?.length ?? 0) === 0 ? (
                  <div className="py-12 text-center px-4">
                    <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-500">No submissions yet</p>
                    <p className="text-xs text-gray-400 mt-1">Team feedback will appear here once submitted.</p>
                    <button
                      onClick={() => setStep('type')}
                      className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Submit feedback →
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {/* Feedback items */}
                    {(allFeedback ?? []).map((item) => {
                      const typeConfig = FEEDBACK_TYPES.find(t => t.value === item.feedback_type);
                      const TypeIcon = typeConfig?.icon ?? MoreHorizontal;
                      const statusCfg = FEEDBACK_STATUS[item.status];
                      const StatusIcon = statusCfg.icon;
                      return (
                        <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className={cn('mt-0.5 flex-shrink-0', typeConfig?.color ?? 'text-gray-400')}>
                              <TypeIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-xs font-medium text-gray-700">
                                  {typeConfig?.label ?? item.feedback_type}
                                </span>
                                <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', statusCfg.className)}>
                                  <StatusIcon className="h-3 w-3" />
                                  {statusCfg.label}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 line-clamp-2 leading-snug">
                                {item.description}
                              </p>
                              {item.admin_notes && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
                                  <span className="font-medium">Admin note:</span> {item.admin_notes}
                                </div>
                              )}
                              {/* This widget only ever rendered `admin_notes`, a
                                  single legacy column. Since migration 239 a
                                  feedback item is a THREAD, so replies written in
                                  the real surface were invisible here and the item
                                  read as unanswered ("no reply seen", Ryan
                                  2026-09-16). Rather than duplicate the thread in a
                                  list that would need one fetch per row, point at
                                  where the conversation actually lives — for
                                  those who can open it there: admins, and the
                                  person who sent it. */}
                              {(isAdminUser || (!!currentUserId && item.user_id === currentUserId)) && (
                                <a
                                  href={`/settings/feedback?item=${item.id}`}
                                  className="mt-2 inline-block text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                                >
                                  Open the conversation →
                                </a>
                              )}
                              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1.5 flex-wrap">
                                {item.user_name && <span className="font-medium text-gray-500">{item.user_name}</span>}
                                {item.user_name && <span>·</span>}
                                {item.page_title && <span className="truncate max-w-[120px]">{item.page_title}</span>}
                                {item.page_title && <span>·</span>}
                                <span>{formatRelativeTime(item.created_at)}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Feature suggestion items */}
                    {(allSuggestions ?? []).map((item) => {
                      const statusCfg = SUGGESTION_STATUS[item.status];
                      return (
                        <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex-shrink-0 text-yellow-500">
                              <Lightbulb className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-xs font-medium text-gray-700">Feature Suggestion</span>
                                <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', statusCfg.className)}>
                                  {statusCfg.label}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-800 leading-snug">{item.title}</p>
                              <p className="text-sm text-gray-500 line-clamp-1 leading-snug mt-0.5">
                                {item.description}
                              </p>
                              {item.admin_notes && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
                                  <span className="font-medium">Admin note:</span> {item.admin_notes}
                                </div>
                              )}
                              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1.5 flex-wrap">
                                {item.user_name && <span className="font-medium text-gray-500">{item.user_name}</span>}
                                {item.user_name && <span>·</span>}
                                <span>{formatRelativeTime(item.created_at)}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Form */}
            {step === 'form' && (
              <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Screenshot Preview */}
                {screenshot && (
                  <div className="relative">
                    <div
                      className="relative border border-gray-200 rounded-lg overflow-hidden cursor-crosshair"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                    >
                      <img
                        src={screenshot}
                        alt="Screenshot"
                        className="w-full h-auto max-h-48 object-contain bg-gray-100"
                      />

                      {/* Selection overlay */}
                      {(currentSelection || selectedRegion) && (
                        <div
                          className="absolute border-2 border-red-500 bg-red-500/10"
                          style={{
                            left: (currentSelection || selectedRegion)!.x,
                            top: (currentSelection || selectedRegion)!.y,
                            width: (currentSelection || selectedRegion)!.width,
                            height: (currentSelection || selectedRegion)!.height,
                          }}
                        />
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <button
                        onClick={() => setIsSelecting(!isSelecting)}
                        className={cn(
                          'flex items-center gap-1 text-xs px-2 py-1 rounded',
                          isSelecting ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'
                        )}
                      >
                        <Crop className="h-3 w-3" />
                        {isSelecting ? 'Selecting...' : 'Highlight Area'}
                      </button>
                      <button
                        onClick={() => {
                          setScreenshot(null);
                          setSelectedRegion(null);
                        }}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 px-2 py-1 rounded hover:bg-gray-100"
                      >
                        <Trash2 className="h-3 w-3" />
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* Pinpoint a page element — works with or without a screenshot */}
                {pinnedElement ? (
                  <div className="flex items-start justify-between gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1 text-xs font-medium text-primary-700">
                        <Crosshair className="h-3 w-3 shrink-0" /> Pinpointed element
                      </p>
                      <p className="truncate text-xs text-gray-700" title={pinnedElement.label}>
                        {pinnedElement.label}
                      </p>
                      <p className="truncate font-mono text-[10px] text-gray-400" title={pinnedElement.selector}>
                        {pinnedElement.selector}
                      </p>
                    </div>
                    <button
                      onClick={() => setPinnedElement(null)}
                      className="shrink-0 text-gray-400 hover:text-red-600"
                      aria-label="Remove pinpointed element"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setIsOpen(false); setIsPicking(true); }}
                    className="flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 hover:border-primary-400 hover:text-primary-700"
                  >
                    <Crosshair className="h-3.5 w-3.5" />
                    Pinpoint an element on the page
                  </button>
                )}

                {/* Feedback Type Badge */}
                <div className="flex items-center gap-2">
                  {(() => {
                    const type = FEEDBACK_TYPES.find(t => t.value === feedbackType);
                    if (!type) return null;
                    return (
                      <>
                        <type.icon className={cn('h-4 w-4', type.color)} />
                        <span className="text-sm font-medium text-gray-700">{type.label}</span>
                      </>
                    );
                  })()}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Please describe what you experienced..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                  />
                </div>

                {/* Page Info */}
                <div className="text-xs text-gray-400 bg-gray-50 p-2 rounded">
                  <div className="truncate">Page: {document.title}</div>
                  <div className="truncate">URL: {window.location.pathname}</div>
                </div>

                {/* Submit */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setStep('type')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!description.trim() || createFeedback.isPending}
                    className="flex-1"
                  >
                    {createFeedback.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Submit
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>,
        document.body
      )}

      {/* Hidden canvas for screenshot capture */}
      <canvas ref={canvasRef} className="hidden" />
    </>
  );
}
